import {
  Component,
  inject,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core'

import { Tool, GeneratedContent } from '@cyborg-monorepo/hunter/common'
import { IconComponent, TypographyComponent, BadgeComponent } from '@hyperkit'

import { getToolIcon } from '../../../../../helpers/utils'
import { QueryStatusColorPipe } from '../../../../../modules/shared/pipes/query-status-color.pipe'
import { NavigationService } from '../../../services/navigation.service'
import {
  buildSelectedQueryId,
  QueryClickEvent,
} from '../../../services/utils/navigation.utils'

import { PivotDropdownComponent } from './pivot-dropdown/pivot-dropdown.component'

@Component({
  selector: 'hunt-query-card',
  standalone: true,
  imports: [
    IconComponent,
    TypographyComponent,
    PivotDropdownComponent,
    BadgeComponent,
    QueryStatusColorPipe,
  ],
  templateUrl: './hunt-query-card.component.html',
  styleUrls: ['./hunt-query-card.component.scss'],
  host: {
    '(click)': '$event.stopPropagation()',
  },
})
export class HuntQueryCardComponent implements OnChanges {
  @Input() tool!: Tool
  @Input() selectedQueryId: string | null = null
  @Output() queryClick = new EventEmitter<QueryClickEvent>()

  isOpen = true
  private readonly nav = inject(NavigationService)

  readonly buildSelectedQueryId = buildSelectedQueryId.bind(this.nav)
  readonly getToolIcon = getToolIcon

  ngOnChanges(_: SimpleChanges) {
    // preserve open state; selection changes should not auto-open cards
  }
  /** All top‐level queries */
  get queries(): GeneratedContent[] {
    return this.tool.generatedContent.Hunt ?? []
  }

  /** true if this query has pivots */
  hasPivots(q: GeneratedContent): boolean {
    return !!q.pivots?.length
  }

  /** Displayable title */
  title(q: GeneratedContent): string {
    return q.title || q.category || 'Untitled Query'
  }

  /** Active when no pivot path and matches selectedQueryId */
  isActive(q: GeneratedContent): boolean {
    return (
      this.nav.pivotPath().length === 0 &&
      this.selectedQueryId === this.buildSelectedQueryId(q.id!)
    )
  }

  trackByQuery(_: number, q: GeneratedContent): string {
    return q.id!
  }

  toggleOpen() {
    this.isOpen = !this.isOpen
  }

  /**
   * Handles both top‐level and pivot clicks.
   * Emits, updates URL via NavigationService,
   * and forwards to any parent listeners.
   */
  onSelect(evt: QueryClickEvent) {
    // this.isOpen = true
    this.nav.handleQueryClick(evt)
    this.queryClick.emit(evt)
  }

  /** Convenience: build a `query` event for a top‐level click */
  onQueryClick(q: GeneratedContent) {
    const evt: QueryClickEvent = {
      type: 'query',
      toolId: this.tool.id,
      queryId: q.id!,
      pivotPath: [],
      pivotKey: q.id!,
    }
    this.onSelect(evt)
  }
  /** Truncate string to maxLength characters, adding "..." if truncated */
  truncate(text: string | null | undefined, maxLength = 40): string {
    if (!text) return ''
    return text.length <= maxLength ? text : text.slice(0, maxLength) + '...'
  }
}
